import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOW = ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
const FILL = { "ح": "FFD1FAE5", "ت": "FFFEF3C7", "غ": "FFFECACA", "إ": "FFDBEAFE", "س": "FFEDE9FE", "ر": "FFE0E7FF", "—": "FFF1F5F9" };
const solid = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });
const border = { top: { style: "thin", color: { argb: "FFCBD5E1" } }, bottom: { style: "thin", color: { argb: "FFCBD5E1" } }, left: { style: "thin", color: { argb: "FFCBD5E1" } }, right: { style: "thin", color: { argb: "FFCBD5E1" } } };

const styleHead = (row) => row.eachCell((c) => { c.font = { bold: true, color: { argb: "FFFFFFFF" } }; c.fill = solid("FF0F766E"); c.alignment = { horizontal: "center", vertical: "middle", wrapText: true }; c.border = border; });

async function build(d, company) {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const sum = wb.addWorksheet("ملخص الرواتب", { views: [{ rightToLeft: true, state: "frozen", ySplit: 3 }] });
  const cols = ["#", "الاسم", "الرقم الوظيفي", "رقم الهوية", "الفرع", "المهنة", "الحالة", "أيام الحضور", "مرات التأخير", "دقائق التأخير", "غياب مسجّل", "غياب (بدون تسجيل)", "إجمالي الغياب", "إجازة", "استئذان", "ساعات العمل"];
  sum.mergeCells(1, 1, 1, cols.length);
  sum.getCell(1, 1).value = `${company || ""} — كشف حضور شهر ${d.month} للرواتب`;
  sum.getCell(1, 1).font = { bold: true, size: 14 };
  sum.getCell(1, 1).alignment = { horizontal: "center" };
  const work = d.days.filter((x) => x.kind === "work").length;
  sum.mergeCells(2, 1, 2, cols.length);
  sum.getCell(2, 1).value = `أيام العمل حتى اليوم: ${work} · العطل الرسمية: ${d.days.filter((x) => x.kind === "holiday").map((x) => `${x.day} (${x.holiday})`).join("، ") || "لا يوجد"} · الغياب بدون تسجيل = يوم عمل لم يُسجَّل فيه حضور`;
  sum.getCell(2, 1).font = { size: 10, color: { argb: "FF64748B" } };
  styleHead(sum.addRow(cols));
  d.employees.forEach((e, i) => {
    const r = sum.addRow([i + 1, e.name, e.employee_number, e.iqama_number, e.branch, e.profession, e.status, e.present + e.late, e.late, e.late_minutes, e.absent, e.implied_absent, e.absent + e.implied_absent, e.leave, e.permission, e.hours]);
    r.eachCell((c) => { c.border = border; c.alignment = { horizontal: "center" }; });
    r.getCell(2).alignment = { horizontal: "right" };
    if (e.absent + e.implied_absent) r.getCell(13).fill = solid("FFFECACA");
    if (e.late) r.getCell(9).fill = solid("FFFEF3C7");
  });
  [5, 26, 14, 14, 16, 16, 12, 11, 11, 11, 11, 13, 12, 9, 9, 11].forEach((w, i) => { sum.getColumn(i + 1).width = w; });

  const grid = wb.addWorksheet("الجدول اليومي", { views: [{ rightToLeft: true, state: "frozen", xSplit: 2, ySplit: 2 }] });
  styleHead(grid.addRow(["الاسم", "الفرع", ...d.days.map((x) => x.day), "ح", "ت", "غ", "إ", "س"]));
  const r2 = grid.addRow(["", "", ...d.days.map((x) => DOW[x.dow]), "", "", "", "", ""]);
  r2.eachCell((c) => { c.font = { size: 9, color: { argb: "FF475569" } }; c.alignment = { horizontal: "center" }; c.border = border; });
  d.employees.forEach((e) => {
    const r = grid.addRow([e.name, e.branch, ...e.cells, e.present, e.late, e.absent + e.implied_absent, e.leave, e.permission]);
    r.eachCell({ includeEmpty: true }, (c, n) => {
      c.border = border; c.alignment = { horizontal: n <= 2 ? "right" : "center" };
      const v = n > 2 && n <= 2 + d.days.length ? c.value : null;
      if (v && FILL[v]) c.fill = solid(FILL[v]);
    });
  });
  grid.getColumn(1).width = 26; grid.getColumn(2).width = 14;
  for (let i = 3; i <= 2 + d.days.length; i++) grid.getColumn(i).width = 4.2;
  grid.addRow([]);
  grid.addRow(["الرموز: ح حضور · ت تأخير · غ غياب · إ إجازة · س استئذان · ر عطلة رسمية · — عطلة أسبوعية"]).getCell(1).font = { size: 10, color: { argb: "FF64748B" } };

  const buf = await wb.xlsx.writeBuffer();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  a.download = `حضور-الرواتب-${d.month}.xlsx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

export const AttendancePayrollExport = ({ month, branch, company }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/attendance/payroll-sheet`, { params: { month, ...(branch ? { branch } : {}) } });
      if (!data.employees.length) { toast.error("لا يوجد موظفون لهذا الشهر"); return; }
      await build(data, company);
      toast.success(`تم تصدير ${data.employees.length} موظف`);
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر التصدير"); } finally { setBusy(false); }
  };
  return (
    <Button onClick={go} disabled={busy} className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="att-payroll-export">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} تصدير إكسل للرواتب
    </Button>
  );
};
